import { Close as CloseIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Chip,
  Link,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { practiceUsersApi } from "apis";
import CustomTable, { CustomTableColumn } from "components/common/CustomTable";
import { EPath } from "constants/path";
import dayjs from "dayjs";
import { formatPhoneFaxNumber } from "helpers";
import { PracticeUserDto } from "libs/cellera-sdk";
import { useCallback, useState, useMemo } from "react";

interface StaffMembersModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly branchId: string;
  readonly branchAddress: string;
}

function StaffMembersModal({
  open,
  onClose,
  branchId,
  branchAddress,
}: StaffMembersModalProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data, isLoading } = useQuery({
    queryKey: ["staff-members", branchId, page, rowsPerPage],
    queryFn: async () => {
      return await practiceUsersApi.listPracticeUsers({
        branchId,
        page: page + 1, // API uses 1-based pagination
        pageSize: rowsPerPage,
      });
    },
    enabled: open && Boolean(branchId),
  });

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    []
  );

  const formatDate = useCallback((dateString: string) => {
    if (!dateString) return "-";
    try {
      return dayjs(dateString).format("MM/DD/YYYY HH:mm:ss");
    } catch {
      return "-";
    }
  }, []);

  const columns: CustomTableColumn<PracticeUserDto>[] = useMemo(
    () => [
      {
        id: "name",
        label: "Name",
        getValue: (user: PracticeUserDto) => (
          <Link
            component="button"
            onClick={() => {
              window.location.href = `${EPath.PRACTICE_DETAIL.replace(":practiceId", user.id)}`;
            }}
            sx={{
              textDecoration: "underline",
              cursor: "pointer",
              color: "primary.main",
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
              "&:hover": {
                color: "primary.dark",
              },
            }}
          >
            {user.name}
          </Link>
        ),
        minWidth: 150,
      },
      {
        id: "phoneNumber",
        label: "Phone Number",
        getValue: (user: PracticeUserDto) =>
          formatPhoneFaxNumber(user.phoneNumber) || "-",
        minWidth: 140,
      },
      {
        id: "role",
        label: "Role",
        getValue: (user: PracticeUserDto) => user.role || "Staff",
        minWidth: 100,
      },
      {
        id: "status",
        label: "Status",
        getValue: (user: PracticeUserDto) => (
          <Chip
            label={user.status === "active" ? "ACTIVE" : "INACTIVE"}
            color={user.status === "active" ? "success" : "error"}
            size="small"
          />
        ),
        minWidth: 100,
      },
      {
        id: "addedDate",
        label: "Added Date",
        getValue: (user: PracticeUserDto) => formatDate(user.addedDate),
        minWidth: 160,
      },
      {
        id: "actions",
        label: "",
        getValue: (user: PracticeUserDto) => (
          <Box
            component="button"
            onClick={() => {
              window.location.href = `${EPath.PRACTICE_DETAIL.replace(":practiceId", user.id)}`;
            }}
            sx={{
              border: "1px solid #1976d2",
              borderRadius: "4px",
              backgroundColor: "transparent",
              color: "#1976d2",
              padding: "6px 12px",
              fontSize: "0.875rem",
              fontWeight: 500,
              cursor: "pointer",
              minWidth: "auto",
              "&:hover": {
                backgroundColor: "#1976d2",
                color: "white",
              },
              "&:focus": {
                outline: "2px solid #1976d2",
                outlineOffset: "2px",
              },
            }}
          >
            View Details
          </Box>
        ),
        width: 120, // Fixed width for actions column
      },
    ],
    [formatDate]
  );

  const staffMembers = data?.practiceUsers || [];
  const totalCount = data?.pagination?.total || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">Staff Members – {branchAddress}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box className="flex justify-center items-center py-8">
            <CircularProgress size={48} />
          </Box>
        ) : (
          <CustomTable
            data={staffMembers}
            columns={columns}
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            isLoading={isLoading}
            emptyStateContent={
              <Typography className="text-center py-8 text-gray-500">
                No staff members found for this branch
              </Typography>
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default StaffMembersModal;
